import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '../services/apiService';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { user, isAuthenticated, authenticatedFetch } = useAuth();
  
  // Core data states
  const [lines, setLines] = useState([]);
  const [trucks, setTrucks] = useState({});
  const [pallets, setPallets] = useState({});
  const [savedLoads, setSavedLoads] = useState([]);
  const [rowWeights, setRowWeights] = useState({});
  // Active date for saved loads (default today)
  const [activeDate, setActiveDate] = useState(new Date().toISOString().slice(0,10));
  // Track last loads fetch to avoid tight loops
  const lastLoadsFetchRef = useRef({ key: null, ts: 0 });
  const loadsInFlightRef = useRef(new Map()); // key -> { promise, started }
  const loadsFailureRef = useRef(new Map()); // key -> { ts, error }
  const loadsCacheRef = useRef(new Map()); // key -> { ts, data }
  // Drivers & connections
  const [drivers, setDrivers] = useState([]); // {id,name,certs:string[]}
  const [driverAssignments, setDriverAssignments] = useState({}); // truckId -> driverId
  const [connections, setConnections] = useState([]); // {id, truckId, trailerId}
  
  // UI states
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const saveDebounceRef = useRef(null);

  // Initialize API service with auth context
  useEffect(() => {
    if (isAuthenticated && authenticatedFetch) {
      apiService.setAuthContext({ authenticatedFetch });
    }
  }, [isAuthenticated, authenticatedFetch]);

  // Load all user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      (async () => {
        try {
          await loadAllUserData();
        } catch (e) {
          console.warn('loadAllUserData failed, attempting autosave restore anyway');
        }
        // Try server-side autosave restore (restore drivers even if no selection stored yet)
  try {
          const autosave = await apiService.getAutosave();
          if (autosave) {
            if (Array.isArray(autosave.drivers)) setDrivers(autosave.drivers);
            if (autosave.driverAssignments && typeof autosave.driverAssignments === 'object') setDriverAssignments(autosave.driverAssignments);
            if (Array.isArray(autosave.connections)) setConnections(autosave.connections);
          }
          if (autosave && autosave.selectedLine && autosave.selectedTruck) {
            // If backend returned empty stubs, rehydrate from autosave snapshot
            if ((!lines || lines.length === 0) && Array.isArray(autosave.lines) && autosave.lines.length > 0) {
              setLines(autosave.lines);
            }
            if (autosave.trucks && Object.keys(trucks || {}).length === 0 && Object.keys(autosave.trucks).length > 0) {
              setTrucks(autosave.trucks);
            }
            if (autosave.pallets && Object.keys(autosave.pallets).length > 0) {
              setPallets(prev => ({ ...prev, ...autosave.pallets }));
            }
            if (autosave.rowWeights && Object.keys(autosave.rowWeights).length > 0) {
              setRowWeights(prev => ({ ...prev, ...autosave.rowWeights }));
            }
            // Restore drivers/assignments/connections from autosave if present
            if (Array.isArray(autosave.drivers)) setDrivers(autosave.drivers);
            if (autosave.driverAssignments && typeof autosave.driverAssignments === 'object') setDriverAssignments(autosave.driverAssignments);
            if (Array.isArray(autosave.connections)) setConnections(autosave.connections);

            const line = (lines.length ? lines : autosave.lines || []).find(l => l.line_id === autosave.selectedLine.line_id) || autosave.selectedLine;
            const lineTrucks = trucks[line?.line_id] || autosave.trucks?.[line?.line_id] || [];
            const truck = lineTrucks.find(t => t.truck_id === autosave.selectedTruck.truck_id) || autosave.selectedTruck;

            if (line && truck) {
              saveSelection(line, truck);
              const key = `${line.line_id}_${truck.truck_id}`;
              if (autosave.pallets && autosave.pallets[key]) {
                setPallets(prev => ({ ...prev, [key]: autosave.pallets[key] }));
              }
              if (autosave.rowWeights && autosave.rowWeights[key]) {
                updateRowWeights(line.line_id, truck.truck_id, autosave.rowWeights[key]);
              }
            }
          }
  } catch (e) {
          // Best effort: ignore if no autosave
        }
        // Fallback: draft_data local restore for drivers if still empty
        try {
          if (user && drivers.length === 0) {
            const draftRaw = localStorage.getItem(`draft_data_${user.id}`);
            if (draftRaw) {
              const draft = JSON.parse(draftRaw);
              if (Array.isArray(draft.drivers) && draft.drivers.length > 0) setDrivers(draft.drivers);
              if (draft.driverAssignments && typeof draft.driverAssignments === 'object') setDriverAssignments(draft.driverAssignments);
              if (Array.isArray(draft.connections) && draft.connections.length > 0) setConnections(draft.connections);
            }
          }
        } catch {}
      })();
    } else {
      // Clear data when logged out
      clearAllData();
    }
  }, [isAuthenticated, user]);

  // Auto-save data periodically (every 30 seconds)
  useEffect(() => {
    if (!isAuthenticated) return;

    const autoSaveInterval = setInterval(() => {
      // Avoid pushing completely empty state that could overwrite a valid autosave
      const isEmpty =
        (lines?.length ?? 0) === 0 &&
        Object.keys(trucks || {}).length === 0 &&
        Object.keys(pallets || {}).length === 0 &&
        (drivers?.length ?? 0) === 0 &&
        Object.keys(driverAssignments || {}).length === 0 &&
        (connections?.length ?? 0) === 0;
      if (!isEmpty) {
        autoSaveData();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [isAuthenticated, lines, trucks, pallets]);

  // Debounced autosave on relevant state changes (fast persistence within ~1s)
  useEffect(() => {
    if (!isAuthenticated) return;

    // Avoid saving when everything is empty (first load/no data)
    const isEmpty =
      (lines?.length ?? 0) === 0 &&
      Object.keys(trucks || {}).length === 0 &&
      Object.keys(pallets || {}).length === 0 &&
      (drivers?.length ?? 0) === 0 &&
      Object.keys(driverAssignments || {}).length === 0 &&
      (connections?.length ?? 0) === 0;
    if (isEmpty) return;

    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => {
      autoSaveData();
    }, 800);

    return () => {
      if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    };
  }, [isAuthenticated, lines, trucks, pallets, rowWeights, selectedLine, selectedTruck, drivers, driverAssignments, connections]);

  // Save data before page unload
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isAuthenticated && hasUnsavedChanges()) {
        // Try to save data (best effort)
        autoSaveData();
        
        // Show confirmation dialog
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isAuthenticated, lines, trucks, pallets]);

  const loadAllUserData = async () => {
    setIsLoading(true);
    try {
      // Load lines
      const userLines = await apiService.getLines();
      setLines(userLines);

      // Load trucks for each line
      const allTrucks = {};
      const allPallets = {};
      
      for (const line of userLines) {
        try {
          const lineTrucks = await apiService.getTrucks(line.line_id);
          allTrucks[line.line_id] = lineTrucks;

          // Load pallets for each truck
          for (const truck of lineTrucks) {
            try {
              const truckPallets = await apiService.getPallets(line.line_id, truck.truck_id);
              const key = `${line.line_id}_${truck.truck_id}`;
              allPallets[key] = truckPallets;
            } catch (error) {
              console.error(`Error loading pallets for truck ${truck.truck_id}:`, error);
              allPallets[`${line.line_id}_${truck.truck_id}`] = [];
            }
          }
        } catch (error) {
          console.error(`Error loading trucks for line ${line.line_id}:`, error);
          allTrucks[line.line_id] = [];
        }
      }

      setTrucks(allTrucks);
      setPallets(allPallets);

      // Load saved loads
  const loads = await apiService.getSavedLoads();
      setSavedLoads(loads);

      // Restore selected line and truck from localStorage
      const savedSelection = localStorage.getItem(`selection_${user.id}`);
      if (savedSelection) {
        const { lineId, truckId } = JSON.parse(savedSelection);
        const line = userLines.find(l => l.line_id === lineId);
        if (line) {
          setSelectedLine(line);
          const lineTrucks = allTrucks[lineId] || [];
          const truck = lineTrucks.find(t => t.truck_id === truckId);
          if (truck) {
            setSelectedTruck(truck);
          }
        }
      } else {
        // If no selection, attempt to use autosave snapshot to preselect
        try {
          const autosave = await apiService.getAutosave();
          if (autosave?.selectedLine && autosave?.selectedTruck) {
            const line = userLines.find(l => l.line_id === autosave.selectedLine.line_id) || autosave.selectedLine;
            const lineTrucks = allTrucks[line?.line_id] || autosave.trucks?.[line?.line_id] || [];
            const truck = lineTrucks.find(t => t.truck_id === autosave.selectedTruck.truck_id) || autosave.selectedTruck;
            if (line && truck) {
              saveSelection(line, truck);
              const key = `${line.line_id}_${truck.truck_id}`;
              if (autosave.pallets && autosave.pallets[key]) {
                setPallets(prev => ({ ...prev, [key]: autosave.pallets[key] }));
              }
              if (autosave.rowWeights && autosave.rowWeights[key]) {
                updateRowWeights(line.line_id, truck.truck_id, autosave.rowWeights[key]);
              }
            }
          }
        } catch {}
      }

      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllData = () => {
    setLines([]);
    setTrucks({});
    setPallets({});
    setSavedLoads([]);
    setRowWeights({});
  setDrivers([]);
  setDriverAssignments({});
  setConnections([]);
    setSelectedLine(null);
    setSelectedTruck(null);
    setLastSyncTime(null);
    
    // Clear localStorage for this user
    if (user) {
      localStorage.removeItem(`selection_${user.id}`);
      localStorage.removeItem(`draft_data_${user.id}`);
    }
  };

  const autoSaveData = async () => {
    if (!isAuthenticated) return;

    try {
      // Save draft data to localStorage as backup
  const draftData = {
        lines,
        trucks,
        pallets,
        drivers,
        driverAssignments,
        connections,
        selectedLine: selectedLine?.line_id,
        selectedTruck: selectedTruck?.truck_id,
        timestamp: Date.now()
      };
      
    localStorage.setItem(`draft_data_${user.id}`, JSON.stringify(draftData));
      // Also push to backend autosave (server persistence)
      const key = selectedLine && selectedTruck ? `${selectedLine.line_id}_${selectedTruck.truck_id}` : null;
      const payload = {
        lines,
        trucks,
        pallets,
        rowWeights,
        drivers,
        driverAssignments,
        connections,
        selectedLine: selectedLine || null,
        selectedTruck: selectedTruck || null,
        currentPallets: key ? (pallets[key] || []) : [],
        timestamp: Date.now()
      };
  try { await apiService.saveAutosave(payload); setLastSyncTime(new Date()); } catch {}
      console.log('Auto-saved data locally and server-side');
      
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  };

  const hasUnsavedChanges = () => {
    return (
      lines.length > 0 ||
      Object.keys(trucks).length > 0 ||
      Object.keys(pallets).length > 0 ||
      drivers.length > 0 ||
      Object.keys(driverAssignments).length > 0 ||
      connections.length > 0
    );
  };

  const saveSelection = (line, truck) => {
    setSelectedLine(line);
    setSelectedTruck(truck);
    
    // Persist selection to localStorage
    if (user && line) {
      const selection = {
        lineId: line.line_id,
        truckId: truck?.truck_id || null
      };
      localStorage.setItem(`selection_${user.id}`, JSON.stringify(selection));
    }

  // Best-effort immediate autosave of selection change (non-blocking)
  try { autoSaveData(); } catch {}
  };

  const addLine = async (lineData) => {
    try {
      const newLine = await apiService.createLine(lineData);
      setLines(prev => [...prev, newLine]);
      setTrucks(prev => ({ ...prev, [newLine.line_id]: [] }));
      return newLine;
    } catch (error) {
      console.error('Error adding line:', error);
      throw error;
    }
  };

  const addTruck = async (lineId, truckData) => {
    try {
      const newTruck = await apiService.createTruck(lineId, truckData);
      setTrucks(prev => ({
        ...prev,
        [lineId]: [...(prev[lineId] || []), newTruck]
      }));
      setPallets(prev => ({
        ...prev,
        [`${lineId}_${newTruck.truck_id}`]: []
      }));
      return newTruck;
    } catch (error) {
      console.error('Error adding truck:', error);
      throw error;
    }
  };

  const addPallet = async (lineId, truckId, palletData) => {
    try {
      const newPallet = await apiService.createPallet(lineId, truckId, palletData);
      const key = `${lineId}_${truckId}`;
      setPallets(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), newPallet]
      }));
      return newPallet;
    } catch (error) {
      console.error('Error adding pallet:', error);
      throw error;
    }
  };

  const updateRowWeights = (lineId, truckId, weights) => {
    const key = `${lineId}_${truckId}`;
    setRowWeights(prev => ({
      ...prev,
      [key]: weights
    }));
    
    // Save to localStorage as backup
    if (user) {
      const allWeights = { ...rowWeights, [key]: weights };
      localStorage.setItem(`row_weights_${user.id}`, JSON.stringify(allWeights));
    }
  };

  const saveCurrentLoad = async (loadName, description = '', date) => {
    if (!selectedLine || !selectedTruck) {
      throw new Error('No line or truck selected');
    }

    try {
      const key = `${selectedLine.line_id}_${selectedTruck.truck_id}`;
      const effectiveDate = date || activeDate || new Date().toISOString().slice(0,10);
      const loadData = {
        name: loadName,
        description,
        date: effectiveDate, // ensured YYYY-MM-DD
        line_id: selectedLine.line_id,
        truck_id: selectedTruck.truck_id,
        pallets_data: pallets[key] || [],
        row_weights: rowWeights[key] || {},
        truck_data: selectedTruck,
        line_data: selectedLine
      };
  console.log('[DataContext] Saving load', { endpoint: '/api/loads', loadData });

      const savedLoad = await apiService.saveLoad(loadData);
  console.log('[DataContext] Saved load response', savedLoad);
      setSavedLoads(prev => [...prev, savedLoad]);
      return savedLoad;
    } catch (error) {
      console.error('Error saving load:', error);
      throw error;
    }
  };

  // Debounced autosave for current selected line/truck load
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!selectedLine || !selectedTruck) return;
    const key = `${selectedLine.line_id}_${selectedTruck.truck_id}`;
    const debounceMs = 1500;
    const timer = setTimeout(async () => {
      try {
        const dateStr = activeDate || new Date().toISOString().slice(0,10);
        const existing = savedLoads.find(l => l.line_id === selectedLine.line_id && l.truck_id === selectedTruck.truck_id && l.load_date === dateStr);
        const payload = {
          date: dateStr,
          name: selectedTruck?.name || null,
          description: null,
          line_id: selectedLine.line_id,
          truck_id: selectedTruck.truck_id,
          pallets_data: pallets[key] || [],
          row_weights: rowWeights[key] || {},
          truck_data: selectedTruck,
          line_data: selectedLine
        };
        if (existing) {
          console.log('[DataContext][autosave-load] patch existing', existing.id, payload);
          const updated = await apiService.patchLoad(existing.id, payload);
          setSavedLoads(prev => prev.map(l => l.id === existing.id ? updated : l));
        } else {
          console.log('[DataContext][autosave-load] create new', payload);
          const created = await apiService.saveLoad(payload);
          setSavedLoads(prev => [...prev, created]);
        }
      } catch (e) {
        console.warn('[DataContext][autosave-load] failed', e);
      }
    }, debounceMs);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pallets, rowWeights, selectedLine?.line_id, selectedTruck?.truck_id, activeDate]);

  const loadSavedLoad = async (loadId) => {
    try {
      // Find the load in our saved loads
      const load = savedLoads.find(l => l.id === loadId);
      if (!load) {
        throw new Error('Load not found');
      }

      // Set the line and truck
      const line = lines.find(l => l.line_id === load.line_id);
      const lineTrucks = trucks[load.line_id] || [];
      const truck = lineTrucks.find(t => t.truck_id === load.truck_id);

      if (!line || !truck) {
        throw new Error('Associated line or truck not found');
      }

      saveSelection(line, truck);

      // Load the pallets data
      const key = `${load.line_id}_${load.truck_id}`;
      setPallets(prev => ({
        ...prev,
        [key]: load.pallets_data || []
      }));

      // Load row weights if available
      if (load.row_weights) {
        updateRowWeights(load.line_id, load.truck_id, load.row_weights);
      }

      return load;
    } catch (error) {
      console.error('Error loading saved load:', error);
      throw error;
    }
  };

  const value = {
    // Data
    lines,
    trucks,
    pallets,
    savedLoads,
    rowWeights,
  drivers,
  driverAssignments,
  connections,
    
    // UI State
    selectedLine,
    selectedTruck,
    isLoading,
    lastSyncTime,
    
    // Actions
    loadAllUserData,
    clearAllData,
    saveSelection,
    addLine,
    addTruck,
    addPallet,
    updateRowWeights,
    saveCurrentLoad,
    loadSavedLoad,
    // Driver actions
    addDriver: (driver) => setDrivers(prev => [...prev, driver]),
    removeDriver: (driverId) => {
      setDrivers(prev => prev.filter(d => d.id !== driverId));
      setDriverAssignments(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(tid => { if (next[tid] === driverId) next[tid] = ""; });
        return next;
      });
    },
    assignDriverToTruck: (truckId, driverId) => setDriverAssignments(prev => ({ ...prev, [String(truckId)]: driverId || "" })),
    // Connections actions
    addConnection: (truckId, trailerId) => {
      const t = String(truckId || "");
      const tr = String(trailerId || "");
      if (!t || !tr || t === tr) return;
      setConnections(prev => {
        if (prev.find(c => c.truckId === t || c.trailerId === tr || (c.truckId === t && c.trailerId === tr))) return prev;
        return [...prev, { id: `cmb_${Date.now()}_${Math.floor(Math.random()*1e6)}`, truckId: t, trailerId: tr }];
      });
    },
    removeConnection: (id) => setConnections(prev => prev.filter(c => c.id !== id)),
    // New helpers
    getSavedLoadsByDateRange: async (from, to) => {
      // Skip if not authenticated or auth context not set yet
      if (!isAuthenticated) {
        return [];
      }
      try { apiService.setAuthContext({ authenticatedFetch }); } catch {}
      const key = `${from || ''}|${to || ''}`;
      const now = Date.now();
      // Serve from cache if fresh (<10s)
      const cached = loadsCacheRef.current.get(key);
      if (cached && (now - cached.ts) < 10000) {
        return cached.data;
      }
      // Throttle identical range to at most once per 5s (even if cache expired)
      if (lastLoadsFetchRef.current.key === key && (now - lastLoadsFetchRef.current.ts) < 5000) {
        return cached ? cached.data : savedLoads;
      }
      // Failure backoff: if last failure for this key within 3s, skip
      const lastFail = loadsFailureRef.current.get(key);
      if (lastFail && (now - lastFail.ts) < 3000) {
        return cached ? cached.data : savedLoads;
      }
      // In-flight dedupe
      const inflight = loadsInFlightRef.current.get(key);
      if (inflight) {
        return inflight.promise;
      }
      lastLoadsFetchRef.current = { key, ts: now };
      console.log('[DataContext] Fetching loads', { from, to });
  try {
        const p = apiService.getSavedLoads({ from, to });
        loadsInFlightRef.current.set(key, { promise: p, started: now });
        const loads = await p;
        console.log('[DataContext] Fetched loads count', loads?.length, loads);
        setSavedLoads(loads);
        loadsInFlightRef.current.delete(key);
        loadsFailureRef.current.delete(key);
        loadsCacheRef.current.set(key, { ts: Date.now(), data: loads });
        return loads;
      } catch (e) {
        console.warn('[DataContext] Failed to fetch saved loads', e);
        loadsInFlightRef.current.delete(key);
        loadsFailureRef.current.set(key, { ts: Date.now(), error: e });
        throw e;
      }
    },
    getDailyStats: async (from, to) => apiService.getDailyStats({ from, to }),
    autoSaveData,
  activeDate,
  setActiveDate,
  apiService,
    
    // Getters
    getTrucksForLine: (lineId) => trucks[lineId] || [],
    getPalletsForTruck: (lineId, truckId) => pallets[`${lineId}_${truckId}`] || [],
    getRowWeightsForTruck: (lineId, truckId) => rowWeights[`${lineId}_${truckId}`] || {},
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
